/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/bookings/:id`
 * API Version: 3.0
 * 
 * @param {string} checkin_date - Tanggal checkin
 * @param {string} checkout_date - Tanggal checkout
 * @param {string} rate_plan_id - ID rate plan
 * @returns {object} - Hasil show rate yang telah diformat
 * @example
 * const { data, isLoading, error } = useShowRate("2022-12-31", "2023-01-01", "123");
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { ShowRateResponse } from "../response/ShowRateResponse";

interface ShowRateProps {
    checkin_date: string;
    checkout_date: string;
    rate_plan_id: string;
}

const useShowRate = ({ checkin_date, checkout_date, rate_plan_id }: ShowRateProps) => {
    return useQuery({
        queryKey: ['booking', 'rooms', 'rates', 'showrate', checkin_date, checkout_date, rate_plan_id],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/v3/show-rate`, {
                    params: {
                        checkin_date: checkin_date,
                        checkout_date: checkout_date,
                        rate_plan_id: rate_plan_id,
                    }
                });

                // Validasi response
                const validationResult = ShowRateResponse.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid show rate data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch show rate`, error);
                throw error;
            }
        },
        enabled: !!checkin_date && !!checkout_date && !!rate_plan_id,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useShowRate;